import { useEffect, useRef, useState } from 'react'
import { Phone, PhoneOff, Mic, MicOff, Loader2, MessageSquare, ChevronDown, ChevronUp, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TranscriptDisplay } from '@/entities/call'
import type { CallControls } from './types'

interface CallUIProps extends CallControls {
  variant: 'compact' | 'full'
  showEndedPanel?: boolean
}

export function CallUI({
  variant,
  callState,
  isMuted,
  connectionError,
  statusMessage,
  audioLevel,
  agentConnected,
  transcriptEvents,
  startCall,
  endCall,
  toggleMute,
  closePanel,
  disabled,
  showEndedPanel = false,
}: CallUIProps) {
  const [showTranscript, setShowTranscript] = useState(true)
  const transcriptEndRef = useRef<HTMLDivElement>(null!)

  useEffect(() => {
    if (transcriptEndRef.current && showTranscript) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [transcriptEvents, showTranscript])

  if (variant === 'full') {
    return <FullLayout
      callState={callState}
      isMuted={isMuted}
      connectionError={connectionError}
      statusMessage={statusMessage}
      audioLevel={audioLevel}
      agentConnected={agentConnected}
      transcriptEvents={transcriptEvents}
      startCall={startCall}
      endCall={endCall}
      toggleMute={toggleMute}
      disabled={disabled}
      transcriptEndRef={transcriptEndRef}
    />
  }

  return <CompactLayout
    callState={callState}
    isMuted={isMuted}
    connectionError={connectionError}
    statusMessage={statusMessage}
    audioLevel={audioLevel}
    agentConnected={agentConnected}
    transcriptEvents={transcriptEvents}
    startCall={startCall}
    endCall={endCall}
    toggleMute={toggleMute}
    closePanel={closePanel}
    disabled={disabled}
    showEndedPanel={showEndedPanel}
    showTranscript={showTranscript}
    setShowTranscript={setShowTranscript}
    transcriptEndRef={transcriptEndRef}
  />
}

/* ── Full-page layout (demo page) ── */

interface FullLayoutProps {
  callState: CallControls['callState']
  isMuted: boolean
  connectionError: string | null
  statusMessage: string | null
  audioLevel: number
  agentConnected: boolean
  transcriptEvents: unknown[]
  startCall: () => void
  endCall: () => void
  toggleMute: () => void
  disabled?: boolean
  transcriptEndRef: React.RefObject<HTMLDivElement>
}

function FullLayout({
  callState,
  isMuted,
  connectionError,
  statusMessage,
  audioLevel,
  agentConnected,
  transcriptEvents,
  startCall,
  endCall,
  toggleMute,
  disabled,
  transcriptEndRef,
}: FullLayoutProps) {
  const isActive = callState === 'connecting' || callState === 'connected' || callState === 'disconnecting'

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {transcriptEvents.length === 0 && callState === 'idle' && (
          <div className="flex items-center justify-center h-full text-gray-400 text-body">
            Click the button below to start a voice conversation
          </div>
        )}
        {transcriptEvents.length === 0 && callState === 'ended' && (
          <div className="flex items-center justify-center h-full text-gray-400 text-body">
            Call ended. Click below to start another call.
          </div>
        )}
        {transcriptEvents.length > 0 && (
          <div className="max-w-xl mx-auto">
            <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
            <div ref={transcriptEndRef} />
          </div>
        )}
      </div>

      {(statusMessage || connectionError) && (
        <div className="px-4 py-2 border-t border-gray-100">
          {connectionError && <p className="text-red-500 text-detail text-center">{connectionError}</p>}
          {statusMessage && !connectionError && <p className="text-gray-500 text-detail text-center">{statusMessage}</p>}
        </div>
      )}

      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center justify-center gap-3">
          {isActive && (
            <>
              {callState === 'connecting' && (
                <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
              )}

              <div className="flex gap-0.5 h-8 items-end">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1.5 bg-emerald-500 rounded-full transition-all duration-100"
                    style={{
                      height: `${Math.max(12, audioLevel * 100 * (i + 1) / 5)}%`,
                      opacity: audioLevel > i * 0.2 ? 1 : 0.3,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={toggleMute}
                disabled={callState !== 'connected'}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  isMuted
                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } disabled:opacity-50`}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>

              <button
                onClick={endCall}
                disabled={callState === 'disconnecting'}
                className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors disabled:opacity-50"
                title="End call"
              >
                {callState === 'disconnecting' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <PhoneOff className="w-5 h-5" />
                )}
              </button>

              {agentConnected && (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-detail text-emerald-600 font-medium">Live</span>
                </div>
              )}
            </>
          )}

          {!isActive && (
            <button
              onClick={startCall}
              disabled={disabled}
              className="w-14 h-14 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50"
              title="Start call"
            >
              <Phone className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Compact layout (campaigns page) ── */

interface CompactLayoutProps {
  callState: CallControls['callState']
  isMuted: boolean
  connectionError: string | null
  statusMessage: string | null
  audioLevel: number
  agentConnected: boolean
  transcriptEvents: unknown[]
  startCall: () => void
  endCall: () => void
  toggleMute: () => void
  closePanel: () => void
  disabled?: boolean
  showEndedPanel: boolean
  showTranscript: boolean
  setShowTranscript: (v: boolean) => void
  transcriptEndRef: React.RefObject<HTMLDivElement>
}

function CompactLayout({
  callState,
  isMuted,
  connectionError,
  statusMessage,
  audioLevel,
  agentConnected,
  transcriptEvents,
  startCall,
  endCall,
  toggleMute,
  closePanel,
  disabled,
  showEndedPanel,
  showTranscript,
  setShowTranscript,
  transcriptEndRef,
}: CompactLayoutProps) {
  const isActive = callState === 'connecting' || callState === 'connected' || callState === 'disconnecting'

  if (callState === 'idle') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={startCall}
        disabled={disabled}
        className="h-6 w-6 p-0"
        title="Browser call"
      >
        <Phone className="w-2.5 h-2.5" />
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled
        className={`h-6 w-6 p-0 ${callState === 'ended' ? 'bg-gray-100 border-gray-300' : 'bg-green-100 border-green-300'}`}
        title={callState === 'ended' ? 'Call ended' : 'Call in progress'}
      >
        {callState === 'connecting' ? (
          <Loader2 className="w-2.5 h-2.5 animate-spin" />
        ) : callState === 'ended' ? (
          <PhoneOff className="w-2.5 h-2.5 text-gray-600" />
        ) : (
          <Phone className="w-2.5 h-2.5 text-green-600" />
        )}
      </Button>

      {(isActive || (callState === 'ended' && showEndedPanel)) && (
        <div className="fixed bottom-4 right-4 z-50 w-80">
          <Card
            className={`shadow-lg ${callState === 'ended' ? 'border-gray-200 bg-gray-50' : 'border-green-200 bg-green-50'}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-body flex items-center gap-2">
                  {callState === 'connecting' && (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                      Connecting...
                    </>
                  )}
                  {callState === 'connected' && (
                    <>
                      <div
                        className={`w-2 h-2 rounded-full ${agentConnected ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}
                      />
                      {agentConnected ? 'Agent Connected' : 'Waiting for Agent...'}
                    </>
                  )}
                  {callState === 'disconnecting' && (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                      Disconnecting...
                    </>
                  )}
                  {callState === 'ended' && (
                    <>
                      <PhoneOff className="w-4 h-4 text-gray-600" />
                      Call Ended
                    </>
                  )}
                </CardTitle>
                {callState === 'ended' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closePanel}
                    className="h-6 w-6 p-0 hover:bg-gray-200"
                    title="Close"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              {connectionError && <p className="text-red-600 text-detail mb-2">{connectionError}</p>}
              {statusMessage && <p className="text-gray-600 text-detail mb-2">{statusMessage}</p>}
              {callState !== 'ended' && (
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center gap-1">
                    <div className="flex gap-0.5 h-6 items-end">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1 bg-green-500 rounded-full transition-all duration-100"
                          style={{
                            height: `${Math.max(4, audioLevel * 100 * (i + 1) / 5)}%`,
                            opacity: audioLevel > i * 0.2 ? 1 : 0.3,
                          }}
                        />
                      ))}
                    </div>
                    <span className="text-detail text-gray-600">{isMuted ? 'Muted' : 'Mic'}</span>
                  </div>

                  <div className="flex gap-1 ml-auto">
                    <Button
                      variant={isMuted ? 'destructive' : 'outline'}
                      size="sm"
                      onClick={toggleMute}
                      disabled={callState !== 'connected'}
                      className="h-7 px-2"
                    >
                      {isMuted ? <MicOff className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={endCall}
                      disabled={callState === 'disconnecting'}
                      className="h-7 px-2"
                    >
                      <PhoneOff className="w-3 h-3 mr-1" />
                      End
                    </Button>
                  </div>
                </div>
              )}

              {transcriptEvents.length > 0 && (
                <div className="border-t border-gray-200 pt-2 mt-2">
                  <div
                    className="flex items-center gap-1 cursor-pointer mb-1"
                    onClick={() => setShowTranscript(!showTranscript)}
                  >
                    <MessageSquare className="w-3 h-3 text-gray-500" />
                    <span className="text-detail font-medium text-gray-600">Transcript</span>
                    {showTranscript ? (
                      <ChevronUp className="w-3 h-3 text-gray-400 ml-auto" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-gray-400 ml-auto" />
                    )}
                  </div>
                  {showTranscript && (
                    <div className="max-h-48 overflow-y-auto bg-white rounded p-2 border border-gray-100">
                      <TranscriptDisplay transcript={JSON.stringify(transcriptEvents)} />
                      <div ref={transcriptEndRef} />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}
